import { Component, Optional, OnInit, OnDestroy } from '@angular/core';
import { NbDialogRef, NbCardModule, NbButtonModule, NbSpinnerModule, NbInputModule, NbCheckboxModule, NbFormFieldModule, NbDialogService } from '@nebular/theme';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { KPI } from '../../../@core/api';
import { AssetsService } from '../../../@core/utils/assets.service';
import { combineLatest, of, forkJoin } from 'rxjs';
import { catchError, take, first, switchMap, map } from 'rxjs/operators';
import { ComplianceService } from '../../../@core/utils/compliance.service';
import { StateLevelsService } from '../../../@core/utils/state-levels.service';
import { I18nService } from '../../../@core/utils/i18n.service';
import { YearsService } from '../../../@core/utils/years.service';
import { PerimetersService } from '../../../@core/utils/perimeters.service';
import { QuantityUnitsService } from '../../../@core/utils/quantity-units.service';
import { cleanFilterString, anyWordBeginWith } from '../../../@core/utils/utils.service';
import { BasicDialogComponent } from '../basic-dialog/basic-dialog.component';
import { SaveToastService, ScopeService } from '../../../@core/utils';
import { idMapToArray } from '../../../@core/utils/scope.service';

@Component({
  selector: 'app-attach-equipment-dialog',
  templateUrl: './attach-equipment-dialog.component.html',
  styleUrls: ['./attach-equipment-dialog.component.scss'],
  standalone: true,
  imports: [
    NbCardModule,
    NbButtonModule,
    NbSpinnerModule,
    NbInputModule,
    NbCheckboxModule,
    NbFormFieldModule,
    TranslateModule,
    CommonModule,
    FontAwesomeModule
  ],
  providers: [DatePipe]
})
export class AttachEquipmentDialogComponent implements OnInit, OnDestroy {
  public equipmentList: KPI.MappedAsset[] = [];
  public filteredEquipmentList: KPI.MappedAsset[] = [];
  public loading: boolean = false;
  public searchQuery: string = '';
  public selectedEquipmentIds: Set<number> = new Set();
  public reportId: number | null = null;
  public documentId: number | null = null;
  public asset: KPI.MappedAsset | null = null;
  public siteId: number | null = null;
  public linkedEquipmentIds: number[] = [];
  public multipleChoice: boolean = true;
  public investmentId: number = null;

  private quantityUnitsCache: Map<number, KPI.QuantityUnit> = new Map();
  private superCategoryId: number = null;
  private categoryId: number = null;
  private subCategoryId: number = null;
  private replacementTypeId: number = null;

  constructor(
    @Optional() public ref: NbDialogRef<AttachEquipmentDialogComponent>,
    private assetsService: AssetsService,
    private complianceService: ComplianceService,
    private translate: TranslateService,
    private stateLevelsService: StateLevelsService,
    private i18n: I18nService,
    private datePipe: DatePipe,
    private yearsService: YearsService,
    private perimetersService: PerimetersService,
    private quantityUnitsService: QuantityUnitsService,
    private dialogService: NbDialogService,
    private scope: ScopeService,
    private saveToast: SaveToastService
  ) {
    const context = (this.ref as any)?.context || {};

    this.reportId = context.reportId || null;
    this.documentId = context.documentId || null;
    this.asset = context.asset || null;
    this.siteId = context.siteId || null;
    this.linkedEquipmentIds = context.linkedEquipmentIds || [];
    this.multipleChoice = context.multipleChoice || true;
    if (context.investmentId) this.investmentId = context.investmentId;
    this.superCategoryId = context.superCategoryId || null;
    this.categoryId = context.categoryId || null;
    this.subCategoryId = context.subCategoryId || null;
  }

  ngOnInit(): void {
    this.loadEquipment();
    this.initializeBackdropClick();
    this.loadQuantityUnits();
    this.scope.investmentTypes$.pipe(idMapToArray()).subscribe(
      investmentTypes => {
        const replacementType = investmentTypes.find(invType => invType.replacement);
        if (replacementType) {
          this.replacementTypeId = replacementType.id;
        }
      }
    )
  }

  private loadQuantityUnits(): void {
    this.quantityUnitsService.quantityUnits$.pipe(first()).subscribe(quantityUnits => {
      quantityUnits.forEach(unit => {
        this.quantityUnitsCache.set(unit.id, unit);
      });
    });
  }

  private initializeBackdropClick(): void {
    if (this.ref && (this.ref as any).onBackdropClick) {
      (this.ref as any).onBackdropClick.pipe(take(1)).subscribe(() => {
        this.saveAndClose();
      });
    }
  }

  ngOnDestroy(): void {}

  public loadEquipment(): void {
    if (!this.siteId) {
      if (this.asset?.perimeters && this.asset.perimeters.length > 0) {
        const site = this.asset.perimeters.find(p => !p.level_parent || !p.is_monosite)
          || this.asset.perimeters[0];
        this.siteId = site.id;
      } else {
        this.perimetersService.perimeters$.pipe(
          first(),
          switchMap((perimeters) => {
            if (perimeters && perimeters.length > 0) {
              const sites = perimeters.filter(p => !p.level_parent);
              const site = sites.length > 0 ? sites[0] : perimeters[0];
              this.siteId = site.id;
              return of(true);
            } else {
              this.loadEquipmentFallback();
              return of(false);
            }
          })
        ).subscribe((shouldLoad) => {
          if (shouldLoad && this.siteId) {
            this.loadEquipmentFromSynthesis();
          }
        });
        return;
      }
    }

    this.loadEquipmentFromSynthesis();
  }

  private loadEquipmentFallback(): void {
    this.loading = true;
    this.equipmentList = [];
    this.filteredEquipmentList = [];
    this.loading = false;
  }

  private loadEquipmentFromSynthesis(): void {
    combineLatest([
      of(this.siteId),
      this.yearsService.startYear$.pipe(first())
    ]).pipe(
      switchMap(([perimeterId, year]) => {
        if (!perimeterId || !year) {
          this.equipmentList = [];
          this.filteredEquipmentList = [];
          this.loading = false;
          return of([]);
        }

        if (!this.assetsService.hasCachedEquipment(perimeterId, year)) {
          this.loading = true;
        }

        return this.assetsService.getEquipmentFromSynthesis(perimeterId, year, 0).pipe(
          catchError(() => {
            if (!this.siteId) {
              return of([]);
            }
            return this.assetsService.getSiteAsset(this.siteId, {}).pipe(
              switchMap((assets) => {
                if (!assets || assets.length === 0) {
                  return of([]);
                }
                return this.assetsService.getAssets(assets.map(a => a.id)).pipe(
                  map((mappedAssets) => {
                    return mappedAssets.filter(asset =>
                      (asset.parent === null || asset.parent === undefined) &&
                      asset.step === KPI.ASSET_STEP.ACTIVE
                    );
                  })
                );
              }),
              catchError(() => {
                return of([]);
              })
            );
          }),
        );
      }),
      first(),
    ).subscribe({
      next: (equipment) => {
        const linkedIdsSet = new Set(this.linkedEquipmentIds || []);
        this.equipmentList = (equipment || []).filter(
          eq => !linkedIdsSet.has(eq.id) &&
          // If we attach to an investment, filter out the assets that have a replacement
          !(this.investmentId && this.assetHasReplacement(eq))
        );
        this.sortEquipmentByLastAccess();
        this.searchByKeywords();
        this.loading = false;
      },
      error: () => {
        this.equipmentList = [];
        this.filteredEquipmentList = [];
        this.loading = false;
      }
    });
  }

  public onSearchChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchQuery = target.value;
    this.searchByKeywords();
  }

  public clearSearch(): void {
    this.searchQuery = '';
    this.initLoadedElements();
  }

  public searchByKeywords(): void {
    const searchText = cleanFilterString(this.searchQuery);

    if (!searchText) {
      return this.initLoadedElements();
    }

    this.filteredEquipmentList = this.equipmentList.filter(equipment => {
      if (this.doesElementMatch(equipment, searchText)) {
        return true;
      } else {
        return equipment.children && equipment.children.filter(child => this.doesElementMatch(child, searchText)).length > 0;
      }
    });
  }

  private initLoadedElements(): void {
    this.filteredEquipmentList = [...this.equipmentList];
  }

  private sortEquipmentByLastAccess(): void {
    this.equipmentList.sort((a, b) => {
      const aLastAccess = (a as any).lastAccess || (a as any).last_access;
      const bLastAccess = (b as any).lastAccess || (b as any).last_access;

      if (!aLastAccess && !bLastAccess) {
        return b.id - a.id;
      }
      if (!aLastAccess) {
        return 1;
      }
      if (!bLastAccess) {
        return -1;
      }

      const dateA = aLastAccess instanceof Date ? aLastAccess : new Date(aLastAccess);
      const dateB = bLastAccess instanceof Date ? bLastAccess : new Date(bLastAccess);
      const timeDiff = dateB.getTime() - dateA.getTime();

      return timeDiff !== 0 ? timeDiff : b.id - a.id;
    });
  }


  private doesElementMatch(asset: KPI.MappedAsset, query: string): boolean {
    const hasKeywords = !!(asset.asset_type_object && (asset.asset_type_object as any).keywords);
    const keywords = hasKeywords
      ? (asset.asset_type_object as any).keywords.map((keyword: any) => keyword.name || "")
      : [];

    const stringsToTest: string[] = [
      asset.label || "",
      asset.category?.name || "",
      asset.sub_category?.name || "",
      asset.asset_type_object?.name || "",
      ...keywords,
      asset.maintenance_code || "",
    ];

    return !!stringsToTest.find(string => anyWordBeginWith(string, query));
  }

  public toggleEquipmentSelection(equipmentId: number): void {
    if (this.selectedEquipmentIds.has(equipmentId)) {
      this.selectedEquipmentIds.delete(equipmentId);
    } else {
      this.selectedEquipmentIds.add(equipmentId);
    }
  }

  public isEquipmentSelected(equipmentId: number): boolean {
    return this.selectedEquipmentIds.has(equipmentId);
  }

  private saveAndClose(): void {
    if (!this.reportId && !this.documentId && !this.investmentId) {
      this.ref.close(false);
      return;
    }

    if (this.selectedEquipmentIds.size === 0) {
      this.ref.close(false);
      return;
    }


    const currentLinkedEquipment = new Set(this.linkedEquipmentIds || []);
    const selectedIds = Array.from(this.selectedEquipmentIds);
    selectedIds.forEach(id => currentLinkedEquipment.add(id));

    const newLinkedEquipment = Array.from(currentLinkedEquipment);

    // Document mode: return the merged IDs to the parent to save via document API
    if (this.documentId) {
      this.ref.close(selectedIds);
      return;
    }

    // Investment mode
    if (this.investmentId) {
      this.ref.close(newLinkedEquipment);
      return;
    }

    this.complianceService.patchControlOfficeReport(this.reportId, {
      linked_equipment: newLinkedEquipment
    }).pipe(
      first(),
      switchMap(updatedReport => {
        if (!updatedReport) {
          return of(null);
        }

        return this.complianceService.getReportReservations(this.reportId!).pipe(
          first(),
          switchMap(reserves => {
            if (!reserves || reserves.length === 0) {
              return of(updatedReport);
            }

            const equipmentComplianceRequests = selectedIds.map(equipmentId => {
              const equipment = this.equipmentList.find(eq => eq.id === equipmentId);
              if (equipment) {
                const compliance = (equipment as any).compliance;
                if (compliance) {
                  const complianceId = typeof compliance === 'object' && compliance.id ? compliance.id : compliance;
                  return of({ equipmentId, complianceId: complianceId && complianceId > 0 ? complianceId : null });
                }
              }
              return this.assetsService.getAsset(equipmentId).pipe(
                first(),
                map(asset => {
                  const compliance = (asset as any)?.compliance;
                  const complianceId = compliance && (typeof compliance === 'object' && compliance.id ? compliance.id : compliance);
                  return { equipmentId, complianceId: complianceId && complianceId > 0 ? complianceId : null };
                }),
                catchError(() => of({ equipmentId, complianceId: null }))
              );
            });

            return forkJoin(equipmentComplianceRequests).pipe(
              switchMap(equipmentCompliances => {
                const validCompliances = equipmentCompliances.filter(ec => ec.complianceId !== null);

                if (validCompliances.length === 0) {
                  return of(updatedReport);
                }

                const reserveUpdateRequests = reserves.map(reserve => {
                  const equipmentCompliance = validCompliances[0];

                  return this.complianceService.updateReservation(reserve.id, {
                    compliance: equipmentCompliance.complianceId
                  } as KPI.ReservationUpdatePayload).pipe(
                    catchError(() => of(null))
                  );
                });

                return forkJoin(reserveUpdateRequests).pipe(
                  map(() => updatedReport),
                  catchError(() => of(updatedReport))
                );
              }),
              catchError(() => of(updatedReport))
            );
          }),
          catchError(() => of(updatedReport))
        );
      }),
      catchError(() => of(null))
    ).subscribe(updatedReport => {
      if (updatedReport) {
        this.saveToast.showSaved();
        this.ref.close(true);
      } else {
        this.ref.close(false);
      }
    });
  }

  public getEquipmentThumbnail(equipment: KPI.MappedAsset): string | null {
    const images = equipment.pictures;
    if (!images || images.length === 0) {
      return null;
    }
    const image = images.find(i => i.thumbnail) || images[0];
    if (!image?.thumbnail) {
      return null;
    }
    let thumbnail = image.thumbnail.trim();
    thumbnail = thumbnail.replace(/["']/g, '');
    return thumbnail || null;
  }

  public getPerimeterHierarchy(equipment: KPI.MappedAsset): string {
    if (equipment.perimeters && equipment.perimeters.length > 0) {
      const perimeters = equipment.perimeters;
      const names = perimeters.map(p => p.name).filter(Boolean);
      if (names.length > 1) {
        return names.slice(1).join(" > ");
      }
      return names.join(" > ");
    }

    const buildingObj = (equipment as any).building_object;
    if (buildingObj && buildingObj.monosite_perimeter) {
      const monositePerimeter = buildingObj.monosite_perimeter;
      if (typeof monositePerimeter === 'object' && monositePerimeter.name) {
        return monositePerimeter.name;
      }
    }

    if (buildingObj) {
      if (buildingObj.name) {
        return buildingObj.name;
      }
      if (buildingObj.site_name) {
        return buildingObj.site_name;
      }
    }

    if (equipment.site_name) {
      return equipment.site_name;
    }
    return '-';
  }

  public getLastEvaluation(equipment: KPI.MappedAsset): string {
    let notation: number | null = null;
    let date: Date | null = null;

    if (equipment.last_notation) {
      notation = equipment.last_notation.note;
      date = equipment.last_notation.audit_year
        ? new Date(equipment.last_notation.audit_year, 0, 1)
        : null;

      if (notation === null || notation === undefined) {
        return this.formatTechnicalStateWithDate(
          this.translate.instant('core.utils.state-levels.no_notation'),
          date
        );
      }

      const label = this.stateLevelsService.getTechnicalStateLabelFromNote(notation);
      return this.formatTechnicalStateWithDate(label, date);
    }

    if (equipment.asset_notations && equipment.asset_notations.length > 0) {
      const technicalStateNotations = equipment.asset_notations
        .filter((n: any) => {
          const questionObj = (n as any).audit_question_object;
          return questionObj && questionObj.mnemonic === 'technical_state';
        })
        .sort((a: any, b: any) => (a.audit_year || 0) - (b.audit_year || 0));

      if (technicalStateNotations.length > 0) {
        const lastNotation = technicalStateNotations[technicalStateNotations.length - 1];
        notation = lastNotation.note;
        date = lastNotation.audit_year
          ? new Date(lastNotation.audit_year, 0, 1)
          : null;

        if (notation !== null && notation !== undefined) {
          const label = this.stateLevelsService.getTechnicalStateLabelFromNote(notation);
          return this.formatTechnicalStateWithDate(label, date);
    }
      }
    }

    if ((equipment as any).last_technical_evaluation !== undefined) {
      const levelNum = (equipment as any).last_technical_evaluation;
      notation = this.stateLevelsService.getTechnicalStateNotationFromLevel(levelNum);
      if (notation !== null) {
        date = (equipment as any).last_evaluation_date || (equipment as any).technical_state_changed_on;
        const label = this.stateLevelsService.getTechnicalStateLabelFromNote(notation);
        return this.formatTechnicalStateWithDate(label, date ? new Date(date) : null);
      }
    }

    const level = (equipment as any).level;
    if (level && typeof level === 'string' && level.trim() !== '') {
      date = (equipment as any).technical_state_changed_on
        ? new Date((equipment as any).technical_state_changed_on)
        : null;
      const label = this.getLevelLabelFromString(level);
      return this.formatTechnicalStateWithDate(label, date);
      }

    date = (equipment as any).last_evaluation_date || (equipment as any).technical_state_changed_on;
    return this.formatTechnicalStateWithDate(
      this.translate.instant('core.utils.state-levels.no_notation'),
      date ? new Date(date) : null
    );
  }

  private formatTechnicalStateWithDate(label: string, date: Date | null): string {
    if (date === null) {
      return label;
    }

    const formattedDate = this.datePipe.transform(date, 'd MMMM yyyy', undefined, this.translate.currentLang) || '';
    return `${this.translate.instant('Last evaluation on')} ${formattedDate}: ${label}`;
  }

  public getLastEvaluationDate(equipment: KPI.MappedAsset): string {
    let date: Date | null = null;
    if (equipment.last_notation) {
      date = equipment.last_notation.audit_year ? new Date(equipment.last_notation.audit_year, 0, 1) : null;
    } else if (equipment.asset_notations && equipment.asset_notations.length > 0) {
      const technicalStateNotations = equipment.asset_notations
        .filter((n: any) => {
          const questionObj = (n as any).audit_question_object;
          return questionObj && questionObj.mnemonic === 'technical_state';
        })
        .sort((a: any, b: any) => (a.audit_year || 0) - (b.audit_year || 0));
      if (technicalStateNotations.length > 0) {
        const lastNotation = technicalStateNotations[technicalStateNotations.length - 1];
        date = lastNotation.audit_year ? new Date(lastNotation.audit_year, 0, 1) : null;
      }
    } else if ((equipment as any).last_technical_evaluation !== undefined) {
      date = (equipment as any).last_evaluation_date || (equipment as any).technical_state_changed_on;
      date = date ? new Date(date) : null;
    } else {
      const level = (equipment as any).level;
      if (level && typeof level === 'string' && level.trim() !== '') {
        date = (equipment as any).technical_state_changed_on ? new Date((equipment as any).technical_state_changed_on) : null;
      } else {
        date = (equipment as any).last_evaluation_date || (equipment as any).technical_state_changed_on;
        date = date ? new Date(date) : null;
      }
    }
    if (date === null) {
      return '';
    }
    return this.datePipe.transform(date, 'd MMMM yyyy', undefined, this.translate.currentLang) || '';
  }

  public getLastEvaluationStatus(equipment: KPI.MappedAsset): string {
    let notation: number | null = null;

    if (equipment.last_notation) {
      notation = equipment.last_notation.note;
      if (notation === null || notation === undefined) {
        return this.translate.instant('core.utils.state-levels.no_notation');
      }
      return this.stateLevelsService.getTechnicalStateLabelFromNote(notation);
    }

    if (equipment.asset_notations && equipment.asset_notations.length > 0) {
      const technicalStateNotations = equipment.asset_notations
        .filter((n: any) => {
          const questionObj = (n as any).audit_question_object;
          return questionObj && questionObj.mnemonic === 'technical_state';
        })
        .sort((a: any, b: any) => (a.audit_year || 0) - (b.audit_year || 0));

      if (technicalStateNotations.length > 0) {
        const lastNotation = technicalStateNotations[technicalStateNotations.length - 1];
        notation = lastNotation.note;
        if (notation !== null && notation !== undefined) {
          return this.stateLevelsService.getTechnicalStateLabelFromNote(notation);
        }
      }
    }

    if ((equipment as any).last_technical_evaluation !== undefined) {
      const levelNum = (equipment as any).last_technical_evaluation;
      notation = this.stateLevelsService.getTechnicalStateNotationFromLevel(levelNum);
      if (notation !== null) {
        return this.stateLevelsService.getTechnicalStateLabelFromNote(notation);
      }
    }

    const level = (equipment as any).level;
    if (level && typeof level === 'string' && level.trim() !== '') {
      return this.getLevelLabelFromString(level);
    }

    return this.translate.instant('core.utils.state-levels.no_notation');
  }

  public isLastEvaluationCritical(equipment: KPI.MappedAsset): boolean {
    const level = (equipment as any).level;
    if (level) {
      const levelLower = level.toLowerCase();
      return levelLower === 'dead' || levelLower === 'critical';
    }

    if (equipment.last_notation) {
      const notation = equipment.last_notation.note;
      if (notation === null || notation === undefined) {
        return true;
      }
      return notation <= 25;
    }

    if ((equipment as any).last_technical_evaluation !== undefined) {
      const levelNum = (equipment as any).last_technical_evaluation;
      const notation = this.stateLevelsService.getTechnicalStateNotationFromLevel(levelNum);
      if (notation !== null) {
        return notation <= 25;
      }
      return false;
    }

    const statusText = this.getLastEvaluationStatus(equipment).toLowerCase();
    const criticalStatuses = [
      this.translate.instant('core.utils.state-levels.critical').toLowerCase(),
      'critique'
    ];

    if (criticalStatuses.some(critical => statusText.includes(critical))) {
      return true;
    }

    const evaluationText = this.getLastEvaluation(equipment);
    return evaluationText.includes(this.translate.instant('core.utils.state-levels.no_notation'));
  }

  private getLevelLabelFromString(level: string): string {
    const levelLower = level.toLowerCase();
    const levelMap: { [key: string]: string } = {
      'dead': 'core.utils.state-levels.critical',
      'critical': 'core.utils.state-levels.critical',
      'bad': 'core.utils.state-levels.deteriorated',
      'ageing': 'core.utils.state-levels.deteriorated',
      'deteriorated': 'core.utils.state-levels.deteriorated',
      'acceptable': 'core.utils.state-levels.acceptable',
      'good': 'core.utils.state-levels.good',
      'new': 'core.utils.state-levels.good'
    };

    const transKey = levelMap[levelLower];
    if (transKey) {
      return this.translate.instant(transKey);
    }
    return this.translate.instant(level) !== level ? this.translate.instant(level) : level;
  }

  public getQuantity(equipment: KPI.MappedAsset): string {
    const quantity = equipment.quantity;
    let quantityUnit: KPI.QuantityUnit | null = null;
    if ((equipment as any).quantity_unit) {
      const unit = (equipment as any).quantity_unit;
      if (typeof unit === 'object' && unit.name) {
        quantityUnit = unit;
      } else if (typeof unit === 'number') {
        quantityUnit = this.quantityUnitsCache.get(unit) || null;
      }
    }
    if (!quantityUnit && equipment.asset_type_object && (equipment.asset_type_object as any).quantity_unit) {
      const unitId = (equipment.asset_type_object as any).quantity_unit;
      if (typeof unitId === 'number') {
        quantityUnit = this.quantityUnitsCache.get(unitId) || null;
      } else if (typeof unitId === 'object' && unitId.name) {
        quantityUnit = unitId as KPI.QuantityUnit;
      }
    }
    const unitName = quantityUnit && quantityUnit.name ? quantityUnit.name : 'u';
    const numQty = Number(quantity ?? 0);
    const formattedQty = isFinite(numQty)
      ? new Intl.NumberFormat(this.i18n.getCulture(), { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(numQty)
      : String(quantity);
    return `${this.translate.instant('Quantity')}: ${formattedQty} ${unitName}`;
  }

  public cancel(): void {
    this.saveAndClose();
  }

  public selectEquipment(equipment: KPI.MappedAsset) {
    if (this.multipleChoice) {
      return;
    }
    // nomenclature check
    if (
      (this.superCategoryId && this.superCategoryId !== equipment.super_category.id) ||
      (this.categoryId && this.categoryId !== equipment.category.id) ||
      (this.subCategoryId && this.subCategoryId !== equipment.sub_category.id)
    ) {
      this.dialogService.open(BasicDialogComponent, {
        context: {
          title: this.translate.instant('For information'),
          content: this.translate.instant('nomenclature_warning'),
          canCancel: true,
        }
      }).onClose.subscribe(res => {
        if (res) {
          this.ref.close([equipment.id]);
        }
      })
    } else {
      // nomenclature ok
      this.ref.close([equipment.id]);
    }
  }

  private assetHasReplacement(asset: KPI.MappedAsset): boolean {
    return !!asset.investments.find(inv => {
      const investmentType = inv.investment_type as any;
      if (typeof investmentType === 'object') {
        return investmentType.id === this.replacementTypeId;
      } else if (typeof investmentType === 'number') {
        return investmentType === this.replacementTypeId;
      } else {
        return false;
      }
    });
  }
}

